import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { GiTigerHead, GiBirdHouse, GiTempleGate } from 'react-icons/gi';
import { MdOutlineNaturePeople } from 'react-icons/md';

const Attractions = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const attractions = [
    {
      name: "Gorewada Bird Sanctuary & Zoo",
      distance: "Nearby",
      time: "Day Trip",
      icon: <GiBirdHouse />,
      description: "Experience diverse bird species and wildlife in their natural habitat",
      image: "https://images.unsplash.com/photo-1437622368342-7a3d73a34c8f?w=400"
    },
    {
      name: "Pench Tiger Reserve",
      distance: "90 km",
      time: "2 hours",
      icon: <GiTigerHead />,
      description: "Famous tiger reserve and inspiration for The Jungle Book",
      image: "https://images.unsplash.com/photo-1602491453631-e2a5ad90a131?w=400"
    },
    {
      name: "Tadoba Andhari Tiger Reserve",
      distance: "140 km",
      time: "3 hours",
      icon: <GiTigerHead />,
      description: "Maharashtra's oldest and largest national park known for tiger sightings",
      image: "https://images.unsplash.com/photo-1615963244664-5b845b2025ee?w=400"
    },
    {
      name: "Umred & Karhandla Tiger Reserve",
      distance: "60 km",
      time: "1.5 hours",
      icon: <MdOutlineNaturePeople />,
      description: "Rich biodiversity and scenic landscapes with wildlife safaris",
      image: "https://images.unsplash.com/photo-1549366021-9f761d450615?w=400"
    },
    {
      name: "Koradi Temple",
      distance: "15 km",
      time: "30 min",
      icon: <GiTempleGate />,
      description: "Sacred temple dedicated to Goddess Mahalakshmi with spiritual significance",
      image: "https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=400"
    }
  ];

  return (
    <section className="section-padding bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block mb-4"
          >
            <FaMapMarkerAlt className="text-red-500 text-5xl mx-auto" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Tourist Attractions
          </h2>
          <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore the beauty and adventure around Navyaa
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {attractions.map((attraction, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={attraction.image}
                  alt={attraction.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                
                {/* Icon Badge */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-primary-600 p-3 rounded-full text-2xl">
                  {attraction.icon}
                </div>

                {/* Distance & Time */}
                <div className="absolute bottom-4 left-4 flex space-x-3">
                  <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-800">
                    {attraction.distance}
                  </span>
                  <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-800">
                    {attraction.time}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {attraction.name}
                </h3>
                <p className="text-gray-600 text-sm">
                  {attraction.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-primary-100 to-nature-moss/20 rounded-2xl p-8 text-center"
        >
          <p className="text-lg text-gray-700 mb-4">
            <strong>Need help planning your adventures?</strong> Our hosts can arrange guided tours, 
            transportation, and provide detailed information about all nearby attractions.
          </p>
          <a href="#contact" className="btn-primary inline-block">
            Plan Your Itinerary
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Attractions;
